/**
 * @file Reads the current user's app-level roles from their Clerk organization
 * membership public metadata (`strive_roles` array).
 *
 * Roles are provisioned at invite time (org invitation public metadata) and
 * copied by Clerk onto the membership when the user accepts — so they are
 * present from the very first login, with no token-refresh lag. A `guest` is
 * restricted to a read-only view of the AEO page.
 */

import { useOrganization } from '@clerk/clerk-react';
import { GUEST_ROLE } from '@/constants';

export interface UserRoleState {
  /** The namespaced app-level roles from membership metadata (e.g. ['strive:guest']). */
  roles: string[];
  /** Whether the current user is a guest (read-only, AEO-only). */
  isGuest: boolean;
  /** Whether the current user is an admin of the active organization. */
  isOrgAdmin: boolean;
  /** Whether the org membership (and thus the roles) has finished loading. */
  isLoaded: boolean;
}

/**
 * Returns the current user's app-level roles derived from their active org
 * membership public metadata.
 *
 * @returns The user role state.
 */
export function useUserRole (): UserRoleState {
  const { membership, isLoaded } = useOrganization();

  const roles = (membership?.publicMetadata as { strive_roles?: string[] } | undefined)?.strive_roles ?? [];

  return {
    roles,
    isGuest: roles.includes(GUEST_ROLE),
    isOrgAdmin: membership?.role === 'org:admin',
    isLoaded,
  };
}
